import { ProfileState, RuleContext } from '@alfresco/adf-extensions';
import { Node, NodeEntry } from '@alfresco/js-api';
import { hasLockedFiles, isContentServiceEnabled } from 'projects/aca-shared/rules/src/app.rules';
import { CustomRuleContext } from '../models/custom-rule-context';
import { Injector } from '@angular/core';
import { APP_CONFIG_PLUGINS, BUILDER_INTEGRATION_SERVICE, BUILDER_PLUGIN, DELETE_ALLOWABLE_OPERATION, EFLOW_INTEGRATION_SERVICE, EFLOW_PLUGIN, ENI_PLUGIN, SIGN_INTEGRATION_SERVICE, SIGN_PLUGIN, SIGNAFORM_INTEGRATION_SERVICE, SIGNAFORM_PLUGIN, SMT_BDOCS_RECORD_TYPE, SMT_BDOCS_STRUCTURAL_FOLDER_ASPECT, SMT_RRHH_RECORD_TYPE, SMT_RRHH_STRUCTURAL_FOLDER_ASPECT, SMT_TAX_GROUPER_DATA_ASPECT, SMT_TAX_TYPE_DATA_ASPECT, TAXONOMY_PLUGIN } from '../models/alden-constants';
import { isNotPlugin, isPluginEnabled } from '../utils/plugin-utils';
import { AldenService } from '../services/alden.service';
import { isFavorites, isNotLibraries, isNotTrashcan, isPreview, isSharedFiles } from '@alfresco/aca-shared/rules';
import { CustomListService } from '../services/custom-list.service';

/**
 * Regla general para desactivar opciones.
 */
export const isDisabled = (_context: RuleContext): boolean => false;

/**
 * Verifica si el usuario puede eliminar los nodos seleccionados.
 *
 * @param context - El contexto de ejecución de las reglas.
 * @returns true si el usuario puede eliminar los nodos seleccionados, false en caso contrario.
 */
export function canDeleteSelection(context: RuleContext): boolean {
  if (
    isNotTrashcan(context) &&
    isNotLibraries(context) &&
    !context.selection.isEmpty
  ) {
    if (hasLockedFiles(context)) {
      return false;
    }

    // Trabajo temporal para la API de Favoritos
    if (isFavorites(context)) {
      return true;
    }

    if (isPreview(context)) {
      return context.permissions.check(context.selection.nodes, [DELETE_ALLOWABLE_OPERATION]);
    }

    // Trabajo temporal para los Archivos Compartidos
    if (isSharedFiles(context)) {
      return context.permissions.check(context.selection.nodes, [DELETE_ALLOWABLE_OPERATION], {
        target: 'allowableOperationsOnTarget'
      });
    }

    return context.permissions.check(context.selection.nodes, [DELETE_ALLOWABLE_OPERATION]);
  }

  return false;
}

/**
 * Verifica si un nodo tiene una operación permitida.
 *
 * @param node - El nodo a verificar.
 * @param allowableOperation - La operación permitida a verificar.
 * @returns true si el nodo tiene la operación permitida, false en caso contrario.
 */
export function isAllowedOperation(node: any, allowableOperation: string): boolean {
  if (node?.entry)
    return !!(node.entry.allowableOperations?.includes(allowableOperation));
  else
    return !!(node?.allowableOperations?.includes(allowableOperation));
}

/**
 * Verifica si un nodo tiene un aspecto específico.
 *
 * @param node - El nodo a verificar.
 * @param aspectName - El nombre del aspecto a verificar.
 * @returns true si el nodo tiene el aspecto específico, false en caso contrario.
 */
export function hasNodeAspect(node: Node | undefined, aspectName: string): boolean {
  return node?.aspectNames?.includes(aspectName) ?? false;
}

/**
 * Verifica si un nodo entry tiene un aspecto específico.
 *
 * @param nodeEntry - El nodo entry a verificar.
 * @param aspectName - El nombre del aspecto a verificar.
 * @returns true si el nodo entry tiene el aspecto específico, false en caso contrario.
 */
export function hasAspect(nodeEntry: NodeEntry, aspectName: string): boolean {
  return hasNodeAspect(nodeEntry.entry, aspectName);
}

/**
 * Verifica si no todos los nodos tienen un aspecto específico.
 *
 * @param nodes - La lista de nodos a verificar.
 * @param aspectName - El nombre del aspecto a verificar.
 * @returns true si no todos los nodos tienen el aspecto específico, false en caso contrario.
 */
export function notHasAspect(nodes: NodeEntry[], aspectName: string): boolean {
  return !nodes.every((node) => hasAspect(node, aspectName));
}

/**
 * Verifica si un nodo entry tiene una propiedad específica.
 *
 * @param node - El nodo a verificar.
 * @param propertyName - El nombre de la propiedad a verificar.
 * @returns true si el nodo tiene la propiedad específica, false en caso contrario.
 */
export function hasProperty(node: NodeEntry, propertyName: string): boolean {
  const properties = node.entry.properties;
  return properties ? !!(properties[propertyName]) : false;
}

/**
 * Verifica si un nodo entry tiene una propiedad específica con un valor específico.
 *
 * @param node - El nodo a verificar.
 * @param propertyName - El nombre de la propiedad a verificar.
 * @param propertyValue - El valor de la propiedad a verificar.
 * @returns true si el nodo tiene la propiedad específica con el valor específico, false en caso contrario.
 */
export function hasPropertyValue(node: NodeEntry, propertyName: string, propertyValue: any): boolean {
  const properties = node.entry.properties;
  return properties ? properties[propertyName] === propertyValue : false;
}

/**
 * Verifica si el perfil del usuario pertenece a un grupo específico.
 *
 * @param profile - El perfil del usuario.
 * @param groupId - El ID del grupo a verificar.
 * @returns true si el perfil del usuario pertenece al grupo específico, false en caso contrario.
 */
export function isInGroup(profile: ProfileState, groupId: string): boolean {
  return (profile?.isAdmin || profile?.groups?.some((group) => group.id === groupId)) || false;
}

/**
 * Verifica si la URL actual incluye '/viewer'.
 *
 * @param context - El contexto de ejecución de las reglas.
 * @returns true si la URL actual incluye '/viewer', false en caso contrario.
 */
export function isViewer(context: RuleContext): boolean {
  const { url } = context.navigation;
  return !!url && url.includes('/viewer') && !url.includes("viewer:view");
}

/**
 * Verifica si la URL actual no incluye '/viewer'.
 *
 * @param context - El contexto de ejecución de las reglas.
 * @returns true si la URL actual no incluye '/viewer', false en caso contrario.
 */
export function isNotViewer(context: RuleContext): boolean {
  return !isViewer(context);
}

/**
 * Verifica si la URL actual incluye un tipo específico.
 *
 * @param context - El contexto de ejecución de las reglas.
 * @param type - El tipo específico a verificar.
 * @returns true si la URL actual incluye el tipo específico, false en caso contrario.
 */
export function isTypeViewer(context: RuleContext, type: string): boolean {
  const { url } = context.navigation;
  return !!(url && url.includes(type));
}

/**
 * Verifica si un nodo es del tipo específico.
 *
 * @param node - El nodo a verificar.
 * @param type - El tipo específico a verificar.
 * @returns true si el nodo es del tipo específico, false en caso contrario.
 */
export function isNodeType(node: Node | undefined, type: string): boolean {
  return node?.nodeType === type;
}

/**
 * Verifica si un nodo entry es del tipo específico.
 *
 * @param nodeEntry - El nodo entry a verificar.
 * @param type - El tipo específico a verificar.
 * @returns true si el nodo entry es del tipo específico, false en caso contrario.
 */
export function isType(nodeEntry: NodeEntry | undefined, type: string): boolean {
  return isNodeType(nodeEntry?.entry, type);
}

/**
 * Verifica si no todos los nodos son del tipo específico.
 *
 * @param nodes - La lista de nodos a verificar.
 * @param type - El tipo específico a verificar.
 * @returns true si no todos los nodos son del tipo específico, false en caso contrario.
 */
export function isNotType(nodes: NodeEntry[], type: string): boolean {
  return !nodes.every((node) => isType(node, type));
}

/**
 * Verifica si el contexto actual es Alden.
 *
 * @param context - El contexto de ejecución de las reglas.
 * @param injector - El inyector de servicios.
 * @returns true si el contexto actual es Alden, false en caso contrario.
 */
export function isAlden(context: RuleContext, injector: Injector): boolean {
  return isContentServiceEnabled()
    && isNotPlugin(context, injector, EFLOW_INTEGRATION_SERVICE)
    && isNotPlugin(context, injector, BUILDER_INTEGRATION_SERVICE)
    && isNotPlugin(context, injector, SIGNAFORM_INTEGRATION_SERVICE)
    && isNotPlugin(context, injector, SIGN_INTEGRATION_SERVICE);
}

/**
 * Verifica si el perfil del usuario es administrador.
 *
 * @param profile - El perfil del usuario.
 * @returns true si el perfil del usuario es administrador, false en caso contrario.
 */
export function isAdmin(profile: ProfileState): boolean {
  return profile.isAdmin;
}

/**
 * Verifica si el usuario tiene permiso para modificar la estructura de carpeta de un producto.
 * @param context El contexto de la regla que contiene la navegación.
 * @returns true si el usuario es administrador o no es una carpeta estructural, false en caso contrario.
 */
export function canModifyStructure(context: RuleContext): boolean {
  const { currentFolder } = context.navigation;
  return isAdmin(context.profile) || !(hasNodeAspect(currentFolder, SMT_RRHH_STRUCTURAL_FOLDER_ASPECT) || isNodeType(currentFolder, SMT_RRHH_RECORD_TYPE)
    || hasNodeAspect(currentFolder, SMT_BDOCS_STRUCTURAL_FOLDER_ASPECT) || isNodeType(currentFolder, SMT_BDOCS_RECORD_TYPE)
    || hasNodeAspect(currentFolder, SMT_TAX_GROUPER_DATA_ASPECT) || hasNodeAspect(currentFolder, SMT_TAX_TYPE_DATA_ASPECT));
}

/**
 * Verifica si no tiene un valor de defecto por defecto.
 *
 * @param context - El contexto de ejecución de las reglas personalizadas.
 * @returns true si no tiene un valor de defecto por defecto, false en caso contrario.
 */
export function hasNotDefaultValue(context: CustomRuleContext): boolean {
  return context.filter.search.settings.defaultValue === undefined;
}

/**
 * Verifica si debe aparecer el botón de Descargar.
 *
 * @param context - El contexto de ejecución de las reglas.
 * @param customListService - El servicio de la tabla del visor.
 * @returns true si hay elementos en la tabla.
 */
export function canDownloadAll(context: RuleContext, customListService: CustomListService): boolean {
  const dataLength = customListService.getDataLengthFromTable();
  return isViewer(context) && dataLength > 0;
}

/**
 * Verifica si debe aparecer el botón de Exportar a Excel.
 *
 * @param context - El contexto de ejecución de las reglas.
 * @param customListService - El servicio de la tabla del visor.
 * @returns true si hay elementos en la tabla.
 */
export function canExcelExport(context: RuleContext, customListService: CustomListService): boolean {
  const dataLength = customListService.getDataLengthFromTable();
  return isViewer(context) && dataLength > 0;
}

export function showLocation(context: RuleContext): boolean {
  return !!context.selection.file?.entry?.isFile && !isTypeViewer(context, 'builder');
}

/**
 * PLUGINS
 */

/**
 * Verifica si el plugin de EFlow está habilitado.
 *
 * @param aldenService - El servicio de Alden que proporciona la configuración del plugin.
 * @returns true si el plugin de EFlow está habilitado, false en caso contrario.
 */
export function isEflowEnabled(aldenService: AldenService): boolean {
  const plugins = aldenService.getAppConfigProp(APP_CONFIG_PLUGINS);
  return isPluginEnabled(plugins, EFLOW_PLUGIN);
}

/**
 * Verifica si el plugin de Builder está habilitado.
 *
 * @param aldenService - El servicio de Alden que proporciona la configuración del plugin.
 * @returns true si el plugin de Builder está habilitado, false en caso contrario.
 */
export function isBuilderEnabled(aldenService: AldenService): boolean {
  const plugins = aldenService.getAppConfigProp(APP_CONFIG_PLUGINS);
  return isPluginEnabled(plugins, BUILDER_PLUGIN);
}

/**
 * Verifica si el plugin de Signaform está habilitado.
 *
 * @param aldenService - El servicio de Alden que proporciona la configuración del plugin.
 * @returns true si el plugin de Signaform está habilitado, false en caso contrario.
 */
export function isSignaformEnabled(aldenService: AldenService): boolean {
  const plugins = aldenService.getAppConfigProp(APP_CONFIG_PLUGINS);
  return isPluginEnabled(plugins, SIGNAFORM_PLUGIN);
}

/**
 * Verifica si el plugin de Taxonomía está habilitado.
 *
 * @param aldenService - El servicio de Alden que proporciona la configuración del plugin.
 * @returns true si el plugin de Taxonomía está habilitado, false en caso contrario.
 */
export function isTaxonomyEnabled(aldenService: AldenService): boolean {
  const plugins = aldenService.getAppConfigProp(APP_CONFIG_PLUGINS);
  return isPluginEnabled(plugins, TAXONOMY_PLUGIN);
}

/**
 * Verifica si el plugin de Sign está habilitado.
 *
 * @param aldenService - El servicio de Alden que proporciona la configuración del plugin.
 * @returns true si el plugin de Sign está habilitado, false en caso contrario.
 */
export function isSignEnabled(aldenService: AldenService): boolean {
  const plugins = aldenService.getAppConfigProp(APP_CONFIG_PLUGINS);
  return isPluginEnabled(plugins, SIGN_PLUGIN);
}

/**
 * Verifica si el plugin de ENI está habilitado.
 *
 * @param aldenService - El servicio de Alden que proporciona la configuración del plugin.
 * @returns true si el plugin de ENI está habilitado, false en caso contrario.
 */
export function isEniEnabled(aldenService: AldenService): boolean {
  const plugins = aldenService.getAppConfigProp(APP_CONFIG_PLUGINS);
  return isPluginEnabled(plugins, ENI_PLUGIN);
}